/**
 * useInScopeTalent — Round 73 (A-C-01)
 *
 * Floo-hierarchy talent visibility hook. Returns all technicians whose active
 * shop membership falls within the requested scope (shop / district / region /
 * organization). Strictly independent from OptiBay Connect.
 *
 * Gated by `canViewInScopeTalent` from `useEnterpriseScope`.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEnterpriseScope } from './useEnterpriseScope';

export type InScopeScopeType = 'shop' | 'district' | 'region' | 'organization';

export interface InScopeTalentRow {
  user_id: string;
  display_name: string;
  technician_profile_id: string | null;
  role: string;
  shop_id: string;
  shop_name: string;
  membership_created_at: string;
  scope_type: string;
  scope_id: string;
  technician_type_title: string;
}

interface UseInScopeTalentArgs {
  scopeType: InScopeScopeType | null;
  scopeId: string | null;
}

export function useInScopeTalent({ scopeType, scopeId }: UseInScopeTalentArgs) {
  const { canViewInScopeTalent } = useEnterpriseScope();

  return useQuery({
    queryKey: ['in-scope-talent', scopeType, scopeId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_in_scope_talent', {
        p_scope_type: scopeType!,
        p_scope_id: scopeId!,
      });
      if (error) throw error;
      return (data ?? []) as InScopeTalentRow[];
    },
    enabled: canViewInScopeTalent && !!scopeType && !!scopeId,
  });
}
